import { useEffect, useMemo } from 'react';
import { userData } from '@api/ai-tools/userData';
import { QueryKeys } from '@constants/queryKeys';
import { useCustomQuery } from '@hooks/core/useCustomQuery';
import { ResponseData } from '@models/common';
import { UserDataModel, UserProfileModel } from '@models/userData';
import { useSplitData } from './useSplitData';

const EMPTY_PROFILE: UserProfileModel = {
  firstName: '',
  lastName: '',
  email: '',
  photoSrc: '',
};

type UseUserDataProps = {
  enabled?: boolean;
};

export const useUserData = ({ enabled = false }: UseUserDataProps = {}) => {
  const { setSplitData } = useSplitData();

  const currentUserUrl = useMemo(() => window.location.href, []);

  const { data, error, refetch } = useCustomQuery<ResponseData<UserDataModel>>(
    [QueryKeys.USER_DATA],
    () => userData({ currentUserUrl }),
    { enabled }
  );

  const userDataModel = data?.data;
  const isUserSubscribed = userDataModel?.isUserSubscribed ?? false;
  const hasWordsBalance =
    !!userDataModel?.wordsBalance &&
    (userDataModel.wordsBalance.isUnlimited ||
      userDataModel.wordsBalance.left > 0);

  useEffect(() => {
    // ES39222 picks the whole screen: settle it on error so the route never
    // hangs on its loader.
    if (error) {
      setSplitData({ isSplitEs39222: false });
    }

    if (!userDataModel) {
      return;
    }

    setSplitData({
      isSplitEs38799: userDataModel.isSplitEs38799,
      isSplitEs38934: userDataModel.isSplitEs38934,
      isSplitEs39123: userDataModel.isSplitEs39123,
      isSplitEs39222: userDataModel.isSplitEs39222,
    });
  }, [userDataModel, error, setSplitData]);

  return {
    error,
    refetch,
    data: userDataModel,
    profile: userDataModel?.profile ?? EMPTY_PROFILE,
    userId: userDataModel?.userId ?? '',
    clientHash: userDataModel?.clientHash ?? '',
    useSafeCopywriting: userDataModel?.useSafeCopywriting ?? true,
    isInitialUserDataUpdated: userDataModel?.isInitialUserDataUpdated ?? false,
    isUserSubscribed,
    hasUserEverHadSubscription:
      userDataModel?.hasUserEverHadSubscription ?? false,
    hasAiToolsAccess: userDataModel?.hasAiToolsAccess ?? false,
    hasActiveSubscription: userDataModel?.hasActiveSubscription ?? false,
    isAppInboxEnabled: userDataModel?.isAppInboxEnabled ?? false,
    hasWordsBalance,
    // Word tools (detector, humanizer, plagiarism) run on words too; every other
    // tool needs a running subscription. PRO features never unlock tools.
    hasWordsToolsAccess: isUserSubscribed || hasWordsBalance,
    isUserLogged: !!userDataModel?.userId,
  };
};
